import React, { useCallback } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import { BadgeSizeType, PageSlice, SliceHeaderWaitlistDetails } from '@contracts';
import { getUspIconsFromData } from '@utils/helpers';
import { getTranslatedHtml } from '@utils/html';
import useNavigate from '@utils/useNavigate';
import Badge from './Badge';
import { TextVariant, Typography } from './design/Typography';
import Waitlist from './design/Waitlist';
import HeaderIcons from './HeaderIcons';
import useDesktop from './useDesktop';

interface Props {
  slice: PageSlice;
  country: CountryCode;
}

const HeaderWaitlist = ({ slice }: Props) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const sliceDetails = slice.primary as SliceHeaderWaitlistDetails;
  const button1Text = sliceDetails.button_text?.text;
  const waitlistId = sliceDetails.waitlist_id;
  const icons = getUspIconsFromData(sliceDetails);
  const isMobile = !useDesktop(1024);
  const onLink1Click = useCallback(() => {
    navigateWrapper(sliceDetails.button_link?.text);
  }, []);

  const renderButtonOrWaitlist = () => {
    if (waitlistId) {
      return (
        <WaitlistWrap>
          {isMobile ? <Typography $variant={TextVariant.UpperText5}>{t('components.first-to-know')}</Typography> : null}
          <Waitlist id={waitlistId} />
        </WaitlistWrap>
      );
    }

    return <ButtonRow>{button1Text && <Button onClick={onLink1Click}>{button1Text}</Button>}</ButtonRow>;
  };

  return (
    <HeaderWaitlistWrap $background={sliceDetails.background?.text || ''}>
      <SlideInner>
        <ContentContainer $color={Colors.navyBlue}>
          <ContentInner $color={Colors.navyBlue}>
            {sliceDetails?.coming_soon ? (
              <ComingSoonWrap>
                <Badge sizeTypes={[BadgeSizeType.Small]} text={t('components.coming-soon')} />
              </ComingSoonWrap>
            ) : null}
            {sliceDetails.subtitle?.text ? <ContentSubtitle>{sliceDetails.subtitle?.text}</ContentSubtitle> : null}
            {sliceDetails.heading?.text ? <ContentTitle>{sliceDetails.heading?.text}</ContentTitle> : null}
            {sliceDetails.info?.text ? (
              <ContentInfo>
                <Typography $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
                  {getTranslatedHtml(sliceDetails.info?.text)}
                </Typography>
              </ContentInfo>
            ) : null}
            {icons?.length ? (
              <USPsWrap>
                <HeaderIcons icons={getUspIconsFromData(sliceDetails)} isVerticalDirection={false} iconSize="25px" />
              </USPsWrap>
            ) : null}
            {renderButtonOrWaitlist()}
          </ContentInner>
        </ContentContainer>
        <ImageContainer>
          <ImageInner>
            <DesktopImage>
              {sliceDetails.image?.gatsbyImageData ? (
                <GatsbyImage image={sliceDetails.image.gatsbyImageData} alt={sliceDetails.image.alt ?? ''} objectFit={'cover'} />
              ) : null}
            </DesktopImage>
            <MobileImage>
              {sliceDetails.mobile_image?.gatsbyImageData ? (
                <GatsbyImage image={sliceDetails.mobile_image.gatsbyImageData} alt={sliceDetails.mobile_image.alt ?? ''} objectFit={'cover'} />
              ) : null}
            </MobileImage>
          </ImageInner>
        </ImageContainer>
      </SlideInner>
    </HeaderWaitlistWrap>
  );
};

export default HeaderWaitlist;

const HeaderWaitlistWrap = styled.div<{ $background: string }>`
  background: ${(props) => (props.$background ? props.$background : 'linear-gradient(180deg, #f9d07c 0%, #fffbe7 100%)')};
  display: flex;
  justify-content: center;
`;

const SlideInner = styled.div`
  display: flex;
  height: 700px;
  max-width: 1440px;
  width: 100%;

  @media (max-width: 1024px) {
    height: auto;
    flex-direction: column-reverse;
    gap: 16px;
    max-width: 100%;
    padding: 30px 16px 82px 16px;
  }
`;

const ContentContainer = styled.div<{ $color: string }>`
  flex: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: left;
  gap: 8px;
  color: ${(props) => props.$color};
  width: 50%;

  @media (max-width: 1024px) {
    position: relative;
    width: 100%;
    max-width: 100%;
    align-items: center;
    text-align: center;
  }
`;

const ContentInner = styled.div<{ $color: string }>`
  width: 480px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  padding: 0;
  gap: 10px;
  color: ${(props) => props.$color};
  z-index: 2;
  margin-left: 120px;

  @media (max-width: 1024px) {
    margin-left: 0;
    position: relative;
    width: 100%;
    max-width: 100%;
    align-items: center;
    text-align: center;
  }
`;

const ComingSoonWrap = styled.div`
  display: flex;

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const USPsWrap = styled.div`
  display: block;
  margin-top: 24px;

  * {
    text-transform: none;
  }

  .icon-rows-wrap > div {
    min-width: 156px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;

  .gatsby-image-wrapper {
    height: 100% !important;
    display: flex !important;
  }

  img {
    width: 720px !important;
    height: 100% !important;
  }

  @media (max-width: 1024px) {
    img {
      width: 100px !important;
      height: 100px !important;
    }
  }
`;

const ImageInner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  max-height: 100%;

  @media (max-width: 1024px) {
    margin-top: 30px;
    position: relative;
  }
`;

const DesktopImage = styled.div`
  width: 100%;
  height: 100%;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileImage = styled.div`
  width: 100%;
  height: 100%;
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    justify-content: center;
  }
`;

const ContentSubtitle = styled.h5`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 15px;
  line-height: 140%;
  display: flex;
  align-items: center;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: inherit;

  @media (max-width: 1024px) {
    font-size: 14px;
  }
`;

const ContentTitle = styled.h2`
  font-family: Domaine;
  font-size: 40px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 56px */
  letter-spacing: -0.4px;

  @media (max-width: 1024px) {
    text-align: center;
    font-size: 34px;
    font-style: normal;
    font-weight: 600;
    line-height: 40px;
  }
`;

const ContentInfo = styled.div`
  margin-top: 8px;
  max-width: 450px;

  @media (max-width: 1024px) {
    text-align: center;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 20px;
`;

const WaitlistWrap = styled.div`
  width: 100%;
  margin-top: 24px;

  > p {
    margin-bottom: 12px;
    width: 100%;
    text-align: left;
  }

  @media (max-width: 1024px) {
    max-width: 500px;
  }
`;
